if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };

      if (!this.elements.viewer) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        this.elements.thumbnails.addEventListener(
          'scroll',
          debounce(() => {
            this.updateThumbnailAccessibility();
          }, 100),
          { passive: true }
        );

        this.elements.thumbnails
          .querySelectorAll('button[name="previous"], button[name="next"]')
          .forEach((button) => {
            button.addEventListener('click', () => {
              window.requestAnimationFrame(() => {
                this.updateThumbnailAccessibility();
              });
            });
          });
      }

      this.updateSlideAccessibility();
    }

    onSlideChanged(event) {
      this.updateSlideAccessibility();
      if (!this.elements.thumbnails) return;

      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);
      this.updateSlideAccessibility();

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    updateSlideAccessibility() {
      const slides = this.elements.viewer.querySelectorAll('[data-media-id]');

      slides.forEach((slide) => {
        const isActive = slide.classList.contains('is-active');
        const focusableElements = slide.querySelectorAll('a[href], button, input, select, textarea, [tabindex]');

        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        focusableElements.forEach((element) => {
          if (isActive) {
            if (element.hasAttribute('data-original-tabindex')) {
              element.setAttribute('tabindex', element.getAttribute('data-original-tabindex'));
              element.removeAttribute('data-original-tabindex');
            } else if (element.getAttribute('tabindex') === '-1') {
              element.removeAttribute('tabindex');
            }
          } else {
            if (element.hasAttribute('tabindex') && !element.hasAttribute('data-original-tabindex')) {
              element.setAttribute('data-original-tabindex', element.getAttribute('tabindex'));
            }
            element.setAttribute('tabindex', '-1');
          }
        });
      });

      this.updateThumbnailAccessibility();
    }

    updateThumbnailAccessibility() {
      if (!this.elements.thumbnails) return;

      const thumbnailSlides = this.elements.thumbnails.querySelectorAll('.thumbnail-list__item.slider__slide');
      const thumbnailsTrack = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails-"]');
      const hasOverflow =
        !!thumbnailsTrack && thumbnailsTrack.scrollWidth > thumbnailsTrack.clientWidth + 1;

      thumbnailSlides.forEach((thumbnailSlide) => {
        const thumbnailButton = thumbnailSlide.querySelector('button');
        if (!thumbnailButton) return;

        let isVisible = true;
        if (hasOverflow) {
          isVisible =
            typeof this.elements.thumbnails.isSlideVisible === 'function'
              ? this.elements.thumbnails.isSlideVisible(thumbnailSlide, 1)
              : true;
        }

        thumbnailSlide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');

        if (isVisible) {
          if (thumbnailButton.hasAttribute('data-original-tabindex')) {
            const originalTabindex = thumbnailButton.getAttribute('data-original-tabindex');
            if (originalTabindex === '') {
              thumbnailButton.removeAttribute('tabindex');
            } else {
              thumbnailButton.setAttribute('tabindex', originalTabindex);
            }
            thumbnailButton.removeAttribute('data-original-tabindex');
          } else if (thumbnailButton.getAttribute('tabindex') === '-1') {
            thumbnailButton.removeAttribute('tabindex');
          }
          return;
        }

        if (thumbnailButton.hasAttribute('tabindex') && !thumbnailButton.hasAttribute('data-original-tabindex')) {
          thumbnailButton.setAttribute('data-original-tabindex', thumbnailButton.getAttribute('tabindex'));
        }

        if (!thumbnailButton.hasAttribute('tabindex')) {
          thumbnailButton.setAttribute('data-original-tabindex', '');
        }

        thumbnailButton.setAttribute('tabindex', '-1');
      });
    }

  });
}
